<script setup lang="ts">
import type { FraudRuleHit, FraudSuspectEvent, SuspectCustomer } from '@/views/customer/fraud/suspectCustomersMock'

const props = defineProps<{
  customer: SuspectCustomer | null
  rule: FraudRuleHit | null
}>()

const open = defineModel<boolean>('open', { required: true })

const { t, locale } = useI18n()

const formatOccurredAt = (value: string | null) =>
  value === null
    ? ''
    : new Date(value).toLocaleString(locale.value, {
        dateStyle: 'short',
        timeStyle: 'short',
        timeZone: 'Europe/Paris'
      })

const ruleLabel = (rule: FraudRuleHit) => t(`pages.customer.fraudSuspects.rules.${rule.slug}.label`)

const ruleDescription = (rule: FraudRuleHit) => t(`pages.customer.fraudSuspects.rules.${rule.slug}.description`)

const eventDetail = (rule: FraudRuleHit, event: FraudSuspectEvent) => {
  const parts = [event.storeName, event.customerName, event.phone, event.subject].filter(
    (part): part is string => part !== null && part !== ''
  )

  if (event.points !== null) {
    const key = rule.slug === 'reward_burst' ? 'pointsUsed' : 'pointsWon'
    parts.push(t(`pages.customer.fraudSuspects.events.${key}`, { count: event.points }))
  }

  if (event.leadMinutes !== null) {
    parts.push(t('pages.customer.fraudSuspects.events.lead', { count: event.leadMinutes }))
  }

  return parts.join(' · ')
}

const isCurrentCustomer = (event: FraudSuspectEvent) =>
  props.customer !== null && event.customerMerchantId === props.customer.id
</script>

<template>
  <Dialog v-model:open="open">
    <DialogContent v-if="customer && rule" class="gap-0 overflow-hidden p-0 sm:max-w-3xl">
      <DialogHeader class="border-b px-6 py-5 pr-12">
        <DialogTitle>{{ customer.name || customer.phone }}</DialogTitle>
        <DialogDescription>
          {{ ruleLabel(rule) }}
          <template v-if="customer.name && customer.phone"> · {{ customer.phone }}</template>
        </DialogDescription>
      </DialogHeader>

      <div class="max-h-[min(70vh,40rem)] overflow-y-auto px-6 py-5">
        <section>
          <p class="mb-4 text-sm text-muted-foreground">{{ ruleDescription(rule) }}</p>
          <p v-if="rule.pattern" class="mb-3 text-sm text-muted-foreground">
            {{ t('pages.customer.fraudSuspects.detail.pattern', { pattern: rule.pattern }) }}
          </p>

          <p v-if="rule.events.length === 0" class="text-sm text-muted-foreground">
            {{ t('pages.customer.fraudSuspects.events.empty') }}
          </p>
          <ul v-else class="divide-y rounded-md border">
            <li v-for="(event, index) in rule.events" :key="`${rule.slug}-${index}`" class="px-3 py-2.5 text-sm">
              <div class="font-medium text-foreground">{{ formatOccurredAt(event.occurredAt) }}</div>
              <div :class="isCurrentCustomer(event) ? 'text-foreground' : 'text-muted-foreground'">
                {{ eventDetail(rule, event) }}
              </div>
            </li>
          </ul>
        </section>
      </div>
    </DialogContent>
  </Dialog>
</template>
